import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { LsqApiServiceListSource } from '@lsq-frontend/angular-shared/api-service';
import { RouteParams, UserStates } from '@lsq-frontend/fasttrack-shared/app-constants';
import { UserStateService } from '@lsq-frontend/fasttrack-shared/features/user-state-service';
import { atlas, CUSTOMER_OVERVIEW } from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { UserStateStatus } from '@lsq-frontend/remote-services/enterprise/common';
import {
    CustomerSummary,
    ListCustomerSummaryRequest,
    ListCustomerSummaryResponse,
    ListCustomerSummaryService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { PopoverPlacement } from 'ngx-smart-popover';
import { convertToTimeZone } from 'date-fns-timezone';
import {
    ClassNames,
    Color,
    FeatureFlagService,
    forLifeOf,
    getDynamicRouteLink,
    getRouterLinkUrlById,
    Icons,
    Interaction,
    LifecycleAware,
    ReportRequestUserSettings,
    ResponsiveService,
    Serializer
} from '@lsq-frontend/common-ui';

@LifecycleAware()
@Component({
    selector: 'customers-by-exposure',
    templateUrl: './customers-by-exposure.component.html',
    styleUrls: ['./customers-by-exposure.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class CustomersByExposureComponent implements OnDestroy, OnInit {
    public title = 'Customers by Exposure';
    public debounceTime = Interaction.SearchDebounceTimeMs;
    public isSmallDeviceStream: Observable<boolean>;
    public customersFiltersFormGroup = new FormGroup({
        customerName: new FormControl()
    });

    public hideExport = true;
    public icons = Icons;
    public color = Color;
    public classNames = ClassNames;
    private _componentIsAlive = false;
    private _userSettingsSavedRequest: ReportRequestUserSettings;

    public dataSource: LsqApiServiceListSource<
        ListCustomerSummaryResponse,
        ListCustomerSummaryRequest
    >;

    public customerOverviewRouterLinkUrl = getRouterLinkUrlById(atlas, CUSTOMER_OVERVIEW);

    public customerHeader = this._i18n({
        value: 'Customer',
        meaning: 'Table column header'
    });
    public arBalanceHeader = this._i18n({
        value: 'AR Balance',
        meaning: 'Table column header'
    });
    public percentOfArHeader = this._i18n({
        value: '% of Total AR',
        meaning: 'Table column header'
    });
    public creditLimitHeader = this._i18n({
        value: 'Credit Limit',
        meaning: 'Table column header'
    });
    public creditUtilizationRateHeader = this._i18n({
        value: 'Credit Utilization Rate',
        meaning: 'Table column header'
    });
    public currentArHeader = this._i18n({
        value: 'Current AR',
        meaning: 'Table column header'
    });
    public ar1To30Header = this._i18n({
        value: 'AR 1-30 Days',
        meaning: 'Table column header'
    });
    public ar31To60Header = this._i18n({
        value: 'AR 31-60 Days',
        meaning: 'Table column header'
    });
    public ar61PlusHeader = this._i18n({
        value: 'AR 61+ Days',
        meaning: 'Table column header'
    });
    public dso12MonthsHeader = this._i18n({
        value: 'DSO (Last 12 Months)',
        meaning: 'Table column header'
    });
    public dso3MonthsHeader = this._i18n({
        value: 'DSO (Last 3 Months)',
        meaning: 'Table column header'
    });

    public readonly noDataTitle = this._i18n('No customers by exposure.');

    public tooltipPlacement = PopoverPlacement.Top;
    public dso3MonthsPopoverContent = this._i18n({
        value: 'Values with asterisks do not reach the full 3 months timeframe.',
        meaning: 'Table column popover'
    });
    public dso12MonthsPopoverContent = this._i18n({
        value: 'Values with asterisks do not reach the full 12 months timeframe.',
        meaning: 'Table column popover'
    });

    constructor(
        private _i18n: I18n,
        private _userStateService: UserStateService,
        private _router: Router,
        private _featureFlagsService: FeatureFlagService,
        private _userService: UserService,
        public listCustomerSummaryService: ListCustomerSummaryService,
        public responsiveService: ResponsiveService,
        public _serializer: Serializer
    ) {
        this.customersFiltersFormGroup.controls.customerName.valueChanges
            .pipe(
                forLifeOf(this),
                distinctUntilChanged(),
                debounceTime(this.debounceTime),
                filter(
                    (customerNameSearch) =>
                        customerNameSearch.length >= Interaction.MinTextSearchLength ||
                        customerNameSearch.length === 0
                )
            )
            .subscribe((customerNameSearch) => {
                this.dataSource.dispatchRequest({
                    searchFilter: customerNameSearch
                });
            });
    }

    public async ngOnInit(): Promise<void> {
        this._componentIsAlive = true;
        this.isSmallDeviceStream = this.responsiveService.smBelow.pipe(distinctUntilChanged());

        const userState = await this._userStateService.getUserState(
            UserStates.ListCustomerSummaryRequest
        );
        this._userStateService.createOrUpdateUserState(
            UserStates.ListCustomerSummaryRequest,
            UserStateStatus.Done,
            {}
        );

        let userSettings: string;
        if (userState) {
            userSettings = userState.jsonData;
        }
        this._userSettingsSavedRequest = userSettings
            ? (this._serializer.deserialize(userSettings) as ReportRequestUserSettings)
            : {};

        const initialRequest = new ListCustomerSummaryRequest({
            companyKey: this._getCompanyKey(),
            asOfDate: convertToTimeZone(new Date(), { timeZone: 'UTC' }),
            skip: 0,
            take: this._userSettingsSavedRequest.take
        });
        this.dataSource = new LsqApiServiceListSource(
            this.listCustomerSummaryService,
            initialRequest
        );
    }

    public ngOnDestroy(): void {
        this._componentIsAlive = false;
    }

    public handleMobileCardClick(row: CustomerSummary): void {
        this._router.navigateByUrl(
            getRouterLinkUrlById(atlas, CUSTOMER_OVERVIEW).replace(
                RouteParams.DebtorKey,
                `${row.thirdPartyKey}`
            )
        );
    }

    public handleTakeChange(newTake: number): void {
        // tslint:disable-next-line: no-floating-promises
        this._userStateService.createOrUpdateUserState(
            UserStates.ListCustomerSummaryRequest,
            UserStateStatus.Done, // TODO: remove when BE makes optional.
            { take: newTake }
        );
    }

    public getCustomerLink(link: string, debtorKey: string): string {
        return getDynamicRouteLink(link, { debtorKey });
    }

    private _getCompanyKey(): string {
        return this._userService.getCompanyKey();
    }
}
